import { maskReplayAttribute } from "./posthogReplayPresentation.js"
import { failClosedPostHogPrivacy, sanitizeLoadedPostHogPersistence } from "./posthogLoadedPrivacy.js"
import { sanitizeReplayDomSnapshot, PRIVATE_REPLAY_CONTROL_SELECTOR } from "./posthogReplayPrivacy.js"
import posthog from "posthog-js"

let initialized = false
let privacyInitializationFailed = false
let exceptionCaptureInstalled = false
let privateRouteActive = false
let privateDestinationPending = false
let sessionRecordingClientInitialized = false
const boundInertiaRouters = new WeakSet()

const PRIVATE_OPENDOOR_PATH = "/opendoor"

function pathFromLocation(value) {
  try {
    const base = typeof window === "undefined" ? "https://analytics.invalid/" : window.location.href
    return new URL(value, base).pathname
  } catch {
    return null
  }
}

function privateOpendoorPathname(pathname) {
  let candidate = pathname
  for (let attempt = 0; attempt < 4; attempt += 1) {
    candidate = candidate.replace(/\\/g, "/")
    candidate = candidate.replace(/^\/{2,}/, "/")
    if (candidate === PRIVATE_OPENDOOR_PATH || candidate.startsWith(`${PRIVATE_OPENDOOR_PATH}/`)) return true
    // A malformed suffix on the exact namespace is ambiguous; suppress the event rather than leak route data.
    if (candidate.toLowerCase().startsWith(`${PRIVATE_OPENDOOR_PATH}%`)) return true

    try {
      const decoded = decodeURIComponent(candidate)
      if (decoded === candidate) return false
      candidate = decoded
    } catch {
      // Try valid ASCII escapes independently so an unrelated malformed segment cannot hide an encoded route.
      const partiallyDecoded = candidate.replace(/%([0-9a-f]{2})/gi, (_match, hex) => String.fromCharCode(Number.parseInt(hex, 16)))
      if (partiallyDecoded === candidate) return false
      candidate = partiallyDecoded
    }
  }

  candidate = candidate.replace(/\\/g, "/").replace(/^\/{2,}/, "/")
  return candidate === PRIVATE_OPENDOOR_PATH || candidate.startsWith(`${PRIVATE_OPENDOOR_PATH}/`)
}

export function isPrivateOpendoorPath(value) {
  if (typeof value !== "string" && !(value instanceof URL)) return false
  const path = pathFromLocation(value)
  if (path !== null) return privateOpendoorPathname(path)

  if (typeof value === "string") {
    const partiallyDecoded = value.replace(/%([0-9a-f]{2})/gi, (_match, hex) => String.fromCharCode(Number.parseInt(hex, 16)))
    return privateOpendoorPathname(partiallyDecoded) || partiallyDecoded.toLowerCase().includes("opendoor")
  }
  return false
}

function privateRouteBlocked(event = null) {
  try {
    if (privateRouteActive || privateDestinationPending) return true
    if (typeof window !== "undefined" && isPrivateOpendoorPath(window.location.pathname)) return true

    const properties = event?.properties
    if (!properties || typeof properties !== "object") return false
    return [
      "$current_url",
      "$pathname",
      "$url",
      "$referrer",
      "$initial_current_url",
      "$initial_referrer",
      "$session_entry_url",
      "current_url",
      "pathname",
      "url",
      "referrer",
    ].some((key) =>
      isPrivateOpendoorPath(properties[key]),
    )
  } catch {
    return true
  }
}

export function isPostHogHelocPrivateRoute(event = null) {
  return privateRouteBlocked(event)
}

export function registerPostHogHelocSessionRecordingClient() {
  sessionRecordingClientInitialized = true
}

function stopHelocSessionRecording() {
  try {
    if (sessionRecordingClientInitialized && typeof posthog.stopSessionRecording === "function") posthog.stopSessionRecording()
  } catch {
    /* analytics must never break the page */
  }
}

function resumeHelocSessionRecording() {
  try {
    if (sessionRecordingClientInitialized && !privateRouteBlocked() && typeof posthog.startSessionRecording === "function") {
      posthog.startSessionRecording(true)
    }
  } catch {
    /* analytics must never break the page */
  }
}

export function bindPostHogHelocNavigation(router) {
  if (!router || typeof router.on !== "function" || boundInertiaRouters.has(router)) return () => {}
  boundInertiaRouters.add(router)

  const removeListeners = []
  removeListeners.push(router.on("before", (event) => {
    const destination = event?.detail?.visit?.url
    const sourceIsPrivate = typeof window !== "undefined" && isPrivateOpendoorPath(window.location.pathname)
    const destinationIsPrivate = isPrivateOpendoorPath(destination)
    privateRouteActive = sourceIsPrivate || destinationIsPrivate
    privateDestinationPending = destinationIsPrivate
    if (privateRouteActive) stopHelocSessionRecording()
  }))
  removeListeners.push(router.on("navigate", (event) => {
    const destination = event?.detail?.page?.url
    const currentIsPrivate = typeof window !== "undefined" && isPrivateOpendoorPath(window.location.pathname)
    privateDestinationPending = false
    privateRouteActive = currentIsPrivate || isPrivateOpendoorPath(destination)
    if (privateRouteActive) stopHelocSessionRecording()
    else resumeHelocSessionRecording()
  }))
  removeListeners.push(router.on("finish", () => {
    if (!privateDestinationPending) return
    const currentIsPrivate = typeof window !== "undefined" && isPrivateOpendoorPath(window.location.pathname)
    privateDestinationPending = false
    privateRouteActive = currentIsPrivate
    if (privateRouteActive) stopHelocSessionRecording()
    else resumeHelocSessionRecording()
  }))

  const onPopstate = () => {
    if (typeof window === "undefined") return
    const destinationIsPrivate = isPrivateOpendoorPath(window.location.pathname)
    if (destinationIsPrivate) {
      privateRouteActive = true
      privateDestinationPending = true
      stopHelocSessionRecording()
    } else {
      privateRouteActive = false
      privateDestinationPending = false
      resumeHelocSessionRecording()
    }
  }
  if (typeof window !== "undefined") window.addEventListener("popstate", onPopstate)

  return () => {
    for (const remove of removeListeners) if (typeof remove === "function") remove()
    if (typeof window !== "undefined") window.removeEventListener("popstate", onPopstate)
    boundInertiaRouters.delete(router)
  }
}

const APPLICANT_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function applicantPhoneDigits(value) {
  const digits = String(value ?? "").replace(/\D+/g, "")
  return /^\d{10}$/.test(digits) ? digits : null
}

export function applicantEmail(value) {
  const normalized = String(value ?? "").trim().toLowerCase()
  return APPLICANT_EMAIL_RE.test(normalized) ? normalized : null
}

export function readPostHogHelocConfig() {
  if (typeof document === "undefined") return null

  const apiKey = document.querySelector('meta[name="posthog-api-key"]')?.getAttribute("content")?.trim()
  if (!apiKey) return null

  const host =
    document.querySelector('meta[name="posthog-host"]')?.getAttribute("content")?.trim() ||
    "https://us.i.posthog.com"

  return { apiKey, host }
}

// Query-string keys that must never reach PostHog: the signed draft capability token, the warm-transfer
// payload ("d"), and borrower PII. Mirrors config/initializers/filter_parameter_logging.rb.
const SENSITIVE_QUERY_PARAM_RE = /draft|token|phone|email|ssn|dob|birth|name|address|income|secret|passw/i
const SENSITIVE_QUERY_PARAM_EXACT = new Set(["d"])
// Non-PII attribution parameters that the pattern above would otherwise strip (lo_name contains "name"). They
// are never stripped, so campaign attribution survives in PostHog.
const SAFE_QUERY_PARAMS = new Set([
  "lo",
  "lo_id",
  "lo_name",
  "offer",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm",
])
const URL_PROPERTY_KEY_RE = /(url|referrer|pathname)$/i
const RRWEB_META_EVENT_TYPE = 4
const RRWEB_PLUGIN_EVENT_TYPE = 6

function isSensitiveQueryParam(pair) {
  const rawKey = pair.split("=")[0]
  let key = rawKey
  try {
    key = decodeURIComponent(rawKey.replace(/\+/g, " "))
  } catch {
    // keep the raw key when it is not valid percent-encoding
  }
  const normalizedKey = key.toLowerCase()
  if (SAFE_QUERY_PARAMS.has(normalizedKey)) return false
  return SENSITIVE_QUERY_PARAM_EXACT.has(normalizedKey) || SENSITIVE_QUERY_PARAM_RE.test(key)
}

function filterQueryPairs(query) {
  return query
    .split("&")
    .filter((pair) => pair !== "" && !isSensitiveQueryParam(pair))
    .join("&")
}

// Removes sensitive query parameters (and key=value pairs in the hash) from an absolute URL, a path, or a
// referrer. Other parameters (utm_*, lo, ...) are kept verbatim so attribution keeps working.
export function sanitizeHelocUrl(url) {
  if (typeof url !== "string" || url === "") return url

  const hashIndex = url.indexOf("#")
  const beforeHash = hashIndex === -1 ? url : url.slice(0, hashIndex)
  const hash = hashIndex === -1 ? "" : url.slice(hashIndex + 1)

  const queryIndex = beforeHash.indexOf("?")
  const base = queryIndex === -1 ? beforeHash : beforeHash.slice(0, queryIndex)
  const query = queryIndex === -1 ? "" : filterQueryPairs(beforeHash.slice(queryIndex + 1))
  const sanitizedHash = hash.includes("=") ? filterQueryPairs(hash) : hash

  return `${base}${query ? `?${query}` : ""}${sanitizedHash ? `#${sanitizedHash}` : ""}`
}

function sanitizeUrlProperties(properties) {
  if (!properties || typeof properties !== "object") return properties

  if (Array.isArray(properties)) return properties.map(sanitizeUrlProperties)
  const sanitized = {}
  for (const [key, value] of Object.entries(properties)) {
    if (key === "$snapshot_data") {
      sanitized[key] = value
      continue
    }
    if (SENSITIVE_QUERY_PARAM_RE.test(key) && !SAFE_QUERY_PARAMS.has(key.toLowerCase()) && !URL_PROPERTY_KEY_RE.test(key)) continue
    if (typeof value === "string" && URL_PROPERTY_KEY_RE.test(key)) {
      sanitized[key] = sanitizeHelocUrl(value)
    } else {
      sanitized[key] = sanitizeUrlProperties(value)
    }
  }
  return sanitized
}

function sanitizeSnapshotEvent(snapshotEvent) {
  if (!snapshotEvent || typeof snapshotEvent !== "object") return snapshotEvent

  if (snapshotEvent.type === RRWEB_META_EVENT_TYPE && typeof snapshotEvent.data?.href === "string") {
    return { ...snapshotEvent, data: { ...snapshotEvent.data, href: sanitizeHelocUrl(snapshotEvent.data.href) } }
  }

  const requests = snapshotEvent.data?.payload?.requests
  if (snapshotEvent.type === RRWEB_PLUGIN_EVENT_TYPE && Array.isArray(requests)) {
    return {
      ...snapshotEvent,
      data: {
        ...snapshotEvent.data,
        payload: { ...snapshotEvent.data.payload, requests: requests.map(maskCapturedHelocNetworkRequest) },
      },
    }
  }

  return sanitizeReplayDomSnapshot(snapshotEvent, sanitizeHelocUrl)
}

export function maskCapturedHelocNetworkRequest(request) {
  if (!request || typeof request !== "object") return request
  const metadata = { ...request }
  delete metadata.requestBody
  delete metadata.responseBody
  if (typeof metadata.name === "string") metadata.name = sanitizeHelocUrl(metadata.name)
  return metadata
}

// posthog-js `before_send`: strips sensitive query strings from $current_url, $referrer, $pathname and the
// other URL-like properties, including the session-replay snapshots (page href and network request URLs).
// If sanitizing fails the event is dropped rather than sent unsanitized.
export function helocBeforeSend(event, configuredProjectKey) {
  if (!event) return event

  try {
    if (privateRouteBlocked(event)) return null
    const sanitizedEvent = { ...event }
    for (const key of ["$set", "$set_once"]) {
      if (Object.hasOwn(event, key)) sanitizedEvent[key] = sanitizeUrlProperties(event[key])
    }
    if (!event.properties) return sanitizedEvent

    const properties = sanitizeUrlProperties(event.properties)
    // SDK transport requires its public project key; no draft/attempt capability is allowed through.
    const projectKey = configuredProjectKey
    if (typeof event.properties.token === "string" && projectKey && event.properties.token === projectKey) {
      properties.token = projectKey
    }
    if (Array.isArray(properties.$snapshot_data)) {
      properties.$snapshot_data = properties.$snapshot_data.map(sanitizeSnapshotEvent)
    }
    return { ...sanitizedEvent, properties }
  } catch {
    return null
  }
}

export function buildPostHogHelocInitOptions(config, onPrivacyReady) {
  return {
    loaded(instance) {
      const ready = sanitizeLoadedPostHogPersistence(instance, sanitizeUrlProperties, sanitizeHelocUrl, () => ({ u: window.location.href, r: document.referrer }))
      onPrivacyReady?.(ready)
    },
    api_host: config.host,
    person_profiles: "always",
    mask_all_element_attributes: true,
    mask_all_text: true,
    capture_pageview: true,
    capture_pageleave: true,
    transport: "fetch",
    // Exceptions are captured by the explicit listeners below so a remote-config toggle can't double-report them.
    capture_exceptions: false,
    before_send: (event) => helocBeforeSend(event, config.apiKey),
    session_recording: {
      // The apply flow collects SSN, DOB, income, phone, email and name: never record input values.
      maskAllInputs: true,
      maskAllElementAttributes: false,
      maskAttributeFn: maskReplayAttribute,
      inlineStylesheet: false,
      blockSelector: `${PRIVATE_REPLAY_CONTROL_SELECTOR},style`,
      // Rendered personal details (review step summary) opt in with this class.
      maskTextClass: "ph-mask",
      maskCapturedNetworkRequestFn: maskCapturedHelocNetworkRequest,
    },
  }
}

export function initPostHogHeloc() {
  if (typeof window === "undefined") return false
  if (privateRouteBlocked()) return false
  if (initialized) return true
  if (privacyInitializationFailed) return false

  const config = readPostHogHelocConfig()
  if (!config) return false

  let privacyReady = false
  try {
    posthog.init(config.apiKey, buildPostHogHelocInitOptions(config, ready => { privacyReady = ready }))
  } catch {
    privacyInitializationFailed = true
    return failClosedPostHogPrivacy(posthog)
  }
  if (!privacyReady) {
    privacyInitializationFailed = true
    return failClosedPostHogPrivacy(posthog)
  }

  initialized = true
  registerPostHogHelocSessionRecordingClient()
  try {
    posthog.startSessionRecording(true)
  } catch {
    /* analytics must never break the page */
  }
  installHelocExceptionCapture()
  return true
}

const MAX_EXCEPTION_MESSAGE_LENGTH = 300

// Error messages occasionally echo user input. Keep the text useful for debugging but drop anything that looks
// like an email address or a long digit run (phone, SSN, card, etc.).
export function scrubExceptionMessage(message) {
  return String(message ?? "")
    .replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, "[email]")
    .replace(/\(?\d[\d\s().-]{6,}\d/g, "[digits]")
    .slice(0, MAX_EXCEPTION_MESSAGE_LENGTH)
}

function exceptionFrom(value, fallbackMessage) {
  const message = scrubExceptionMessage(value?.message ?? (typeof value === "string" ? value : fallbackMessage))
  const error = new Error(message || fallbackMessage)
  if (/^(Error|TypeError|RangeError|ReferenceError|SyntaxError|URIError|EvalError|AggregateError)$/.test(value?.name)) error.name = value.name
  return error
}

export function captureHelocException(value, source, fallbackMessage = "Unknown error") {
  if (privateRouteBlocked()) return
  try {
    posthog.captureException(exceptionFrom(value, fallbackMessage), { heloc_exception_source: source })
  } catch {
    /* analytics must never break the page */
  }
}

export function installHelocExceptionCapture() {
  if (exceptionCaptureInstalled || typeof window === "undefined") return false
  exceptionCaptureInstalled = true

  window.addEventListener("error", (event) => {
    captureHelocException(event?.error ?? event?.message, "window_error", "Script error")
  })
  window.addEventListener("unhandledrejection", (event) => {
    captureHelocException(event?.reason, "unhandled_rejection", "Unhandled promise rejection")
  })
  return true
}

// The draft token is a signed capability (read/edit access to the application), so it is never used as, or
// derived into, a distinct id. Without an application external id there is nothing safe to identify with.
export function helocDistinctId(applicationExternalId) {
  const applicationId = applicationExternalId ? String(applicationExternalId).trim() : ""
  return applicationId || null
}

export function identifyPostHogHelocUser({ applicationExternalId }) {
  if (typeof window === "undefined") return
  if (privateRouteBlocked()) return
  if (!initPostHogHeloc()) return

  const distinctId = helocDistinctId(applicationExternalId)
  if (!distinctId) return

  const properties = { application_external_id: distinctId }

  try {
    posthog.identify(distinctId, properties)
  } catch {
    /* analytics must never break the page */
  }
}

export function captureHelocEvent(name, properties = {}) {
  if (!name) return
  if (privateRouteBlocked()) return
  if (!initPostHogHeloc()) return

  try {
    const event = helocBeforeSend({ properties })
    if (event) posthog.capture(name, event.properties)
  } catch {
    /* analytics must never break the flow */
  }
}

export function bindPostHogHelocIdentifyFromDom() {
  const element = document.getElementById("posthog-heloc-identify")
  if (!element) return

  identifyPostHogHelocUser({
    applicationExternalId: element.dataset.applicationExternalId,
  })
}
